import { useEffect, useRef, useState } from 'react';
import { SeasonLeaderboardData, SeasonWebSocketMessage } from '../models/seasonLeaderboardTypes';
import { environmentVariables } from '../utils/helpers/environmentVariables';

interface ConnectionState {
    status: 'initial' | 'connecting' | 'connected' | 'disconnected' | 'error';
    error?: string;
}

const transformSeasonData = (data: any): SeasonLeaderboardData => {
    const seasonLb = data.seasonLeaderboard;
    const regattaResults = data.seasonRegattaResults;

    if (!seasonLb || !regattaResults) {
        return seasonLb as SeasonLeaderboardData;
    }

    // Build regatta breakdowns from race results
    const standingsWithRegattas = seasonLb.standings.map((standing: any) => ({
        ...standing,
        regattas: regattaResults.standings.map((regatta: any) => {
            // Find Event race for this regatta
            const eventRace = regatta.races?.find((r: any) => r.name === 'Event');
            const winner = eventRace?.winners?.find((w: any) => w.country === standing.teamAbbreviation);

            return {
                regattaId: regatta.regattaId,
                regattaName: regatta.regattaName,
                points: winner?.points || null,
                penaltyPoints: winner?.penaltyPoints || 0,
                rank: null
            };
        })
    }));

    return {
        seasonId: seasonLb.seasonId,
        timestamp: new Date().toISOString(),
        latestRegattaStatus: seasonLb.latestRegattaStatus,
        latestRegattaId: seasonLb.latestRegattaId,
        standings: standingsWithRegattas
    };
};

export const useSeasonLeaderboardWebSocket = (seasonId: string) => {
    const [seasonData, setSeasonData] = useState<SeasonLeaderboardData | null>(null);
    const [connectionState, setConnectionState] = useState<ConnectionState>({ status: 'initial' });
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!seasonId) {
            return;
        }

        const connectWebSocket = () => {
            const websocketRoot = environmentVariables.websocketRoot();
            const websocketToken = environmentVariables.websocketAuthToken();

            if (!websocketRoot || !websocketToken) {
                setConnectionState({
                    status: 'error',
                    error: 'WebSocket configuration missing'
                });
                return;
            }

            // Format seasonId with leading zero (e.g., 5 -> "5", 05 -> "05")
            const formattedSeasonId = seasonId.toString().padStart(1, '0');
            const leaderboardType = 'Multi Season Championships';
            const wsUrl = `${websocketRoot}websocket/season?token=${websocketToken}&seasonId=${formattedSeasonId}&leaderboardType=${encodeURIComponent(leaderboardType)}`;

            setConnectionState({ status: 'connecting' });

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                setConnectionState({ status: 'connected' });
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);

                    // Handle heartbeat messages
                    if (message.type === 'heartbeat') {
                        return;
                    }

                    if (message.type === 'info') {
                        return;
                    }

                    if (message.type === 'error') {
                        setConnectionState({
                            status: 'error',
                            error: message.message
                        });
                        return;
                    }

                    if (message.event === 'SEASON_DATA') {
                        const transformed = transformSeasonData(message.content);
                        setSeasonData(transformed);
                    }
                } catch (error) {
                    setConnectionState({
                        status: 'error',
                        error: 'Error parsing message'
                    });
                }
            };

            ws.onerror = () => {
                setConnectionState({
                    status: 'error',
                    error: 'WebSocket connection error'
                });
            };

            ws.onclose = (event) => {
                setConnectionState({ status: 'disconnected' });

                if (event.code !== 1000) {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connectWebSocket();
                    }, 5000);
                }
            };
        };

        connectWebSocket();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (wsRef.current) {
                wsRef.current.close(1000, 'Component unmounting');
                wsRef.current = null;
            }
        };
    }, [seasonId]);

    return {
        seasonData,
        connectionState
    };
};